import { User } from "@supabase/supabase-js";
import { supabase } from "./supabaseClient";
import { scrypt } from "scrypt-js";

// Helper to convert string to Uint8Array
function encode(text: string): Uint8Array {
  return new TextEncoder().encode(text);
}

// Helper to convert Uint8Array to string
function decode(bytes: ArrayBuffer): string {
  return new TextDecoder().decode(bytes);
}

// Helper: Base64 encode/decode for binary data
function toBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

function fromBase64(base64: string): Uint8Array {
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}

// Save and load derived key and salt to/from local storage
export async function saveKeyAndSaltToSessionStorage(
  key: CryptoKey,
  salt: Uint8Array,
): Promise<void> {
  const keyData = await crypto.subtle.exportKey("raw", key);
  // NOTE: We use localStorage here since sessionStorage will not persist across tabs.
  localStorage.setItem(
    "sessionDerivedKey",
    JSON.stringify({
      keyData: toBase64(new Uint8Array(keyData)),
      saltStr: toBase64(salt),
    }),
  );
}
// Load derived key and salt from local storage
export async function loadKeyAndSaltFromSessionStorage(): Promise<{
  key: CryptoKey;
  salt: Uint8Array;
} | null> {
  const derivedKey = localStorage.getItem("sessionDerivedKey");
  if (!derivedKey) return null;

  const { keyData, saltStr } = JSON.parse(derivedKey);
  const keyDataBytes = fromBase64(keyData);
  const salt = fromBase64(saltStr);
  return {
    key: await crypto.subtle.importKey("raw", keyDataBytes, "AES-GCM", true, [
      "encrypt",
      "decrypt",
    ]),
    salt,
  };
}
export function clearKeyAndSaltFromSessionStorage(): void {
  localStorage.removeItem("sessionDerivedKey");
}

// Derive AES key from password and salt, using scrypt
export async function deriveKey(
  passwordBytes: Uint8Array,
  salt?: Uint8Array,
): Promise<{ key: CryptoKey; salt: Uint8Array }> {
  const _salt = salt ?? crypto.getRandomValues(new Uint8Array(16)); // 128-bit salt

  return scrypt(passwordBytes, _salt, 16384, 8, 1, 32)
    .then((key) =>
      crypto.subtle.importKey("raw", key, "AES-GCM", true, [
        "encrypt",
        "decrypt",
      ]),
    )
    .then((key) => {
      return {
        key,
        salt: _salt,
      };
    });
}

/** Encrypt a JSON object.
 */
export async function encryptJSON(
  data: object,
  derivedKey: CryptoKey,
): Promise<{ encrypted: string; iv: string }> {
  const json = JSON.stringify(data);
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    derivedKey,
    encode(json),
  );

  return {
    encrypted: toBase64(new Uint8Array(ciphertext)),
    iv: toBase64(iv),
  };
}

async function decryptJSONWithKey(
  encrypted: string,
  iv: string,
  key: CryptoKey,
): Promise<any> {
  const ciphertext = fromBase64(encrypted);
  const ivBytes = fromBase64(iv);
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: ivBytes },
    key,
    ciphertext,
  );

  const text = decode(decrypted);
  return JSON.parse(text);
}

/**
 * Decrypt blob to JSON object.
 */
export async function decryptJSON(
  encrypted: string,
  iv: string,
  salt: string,
  passwordBytes: Uint8Array,
): Promise<any> {
  const saltBytes = fromBase64(salt);
  const { key } = await deriveKey(passwordBytes, saltBytes);
  return await decryptJSONWithKey(encrypted, iv, key);
}

/**
 * Fetch and decrypt user settings from the database.
 * @param user The authenticated user.
 * @param passwordBytes The password bytes used for encryption.
 */
export async function getUserSettingsFromDb(
  user: User | null,
  passwordBytes: Uint8Array,
): Promise<object> {
  if (!user) {
    throw new Error("User not authenticated");
  }

  // Fetch the encrypted settings from the database
  const { data, error } = await supabase
    .from("settings")
    .select("*")
    .eq("user_id", user.id)
    .single();

  if (error) throw error;

  // Decrypt the settings using the user password
  return await decryptJSON(
    data.encrypted_data,
    data.iv,
    data.salt,
    passwordBytes,
  );
}

/**
 * Fetch and decrypt user settings from the database using a derived key.
 * NOTE: This is a more secure method than using the password directly,
 * as it avoids exposing the password in memory,
 * but it will fail if the user has started a different session and is somehow still logged in---
 * i.e., if the user last saved their settings via a different salt and key.
 * You should call getUserSettingsFromDb() immediately upon login and then immediately re-save the settings
 * to the database with the new session's salt and key.
 */
export async function getUserSettingsFromDbWithKey(
  user: User | null,
  derivedKey: CryptoKey,
): Promise<object> {
  if (!user) {
    throw new Error("User not authenticated");
  }

  // Fetch the encrypted settings from the database
  const { data, error } = await supabase
    .from("settings")
    .select("*")
    .eq("user_id", user.id)
    .single();

  if (error) throw error;

  // Decrypt the settings using the user password
  return await decryptJSONWithKey(data.encrypted_data, data.iv, derivedKey);
}

/**
 * Send user settings to the database, encrypting them first.
 * @param settings The settings to be stored, as a JSON object.
 * @param user The authenticated user.
 * @param passwordBytes The password bytes used for encryption.
 */
export async function sendUserSettingsToDb(
  settings: object,
  user: User | null,
  derivedKey: CryptoKey,
  salt: Uint8Array,
): Promise<void> {
  if (!user) {
    throw new Error("User not authenticated");
  }
  if (settings == null || derivedKey == null || salt == null) {
    throw new Error("Missing required parameters");
  }
  const userId = user.id;

  // Encrypt the settings using the user password
  const { encrypted, iv } = await encryptJSON(settings, derivedKey);

  // Store the encrypted settings in the database
  const { error } = await supabase.from("settings").upsert(
    {
      user_id: userId,
      encrypted_data: encrypted,
      iv,
      salt: toBase64(salt),
    },
    { onConflict: "user_id" }, // Ensure we don't duplicate for the same user
  );

  if (error) {
    console.error("Error storing encrypted settings:", error);
    throw new Error("Failed to store encrypted settings.");
  }

  console.log("Successfully saved encrypted settings.");
}

// OLD -- uses PBKDF2, which is less safe than scrypt
// async function deriveKey(
//   passwordBytes: Uint8Array,
//   salt: Uint8Array,
// ): Promise<CryptoKey> {
//   const keyMaterial = await crypto.subtle.importKey(
//     "raw",
//     passwordBytes,
//     { name: "PBKDF2" },
//     false,
//     ["deriveKey"],
//   );

//   return crypto.subtle.deriveKey(
//     {
//       name: "PBKDF2",
//       salt,
//       iterations: 100000,
//       hash: "SHA-256",
//     },
//     keyMaterial,
//     { name: "AES-GCM", length: 256 },
//     false,
//     ["encrypt", "decrypt"],
//   );
// }
